import { DateTime, Interval } from "luxon";
import {
  MINUTES_PER_HOUR,
  HALF_HOUR_TICK_THRESHOLD,
  TWO_HOUR_TICK_THRESHOLD,
  TICK_STEP_HALF_HOUR,
  TICK_STEP_HOUR,
  TICK_STEP_TWO_HOURS,
  DEFAULT_SELECTION_MINUTES,
} from "./timePickerConstants";

export type ValidInterval = Interval & { start: DateTime; end: DateTime };

export const isValidInterval = (iv: Interval): iv is ValidInterval =>
  iv.isValid && iv.start !== null && iv.end !== null;

export const dateToMinutes = (dt: DateTime): number =>
  dt.hour * MINUTES_PER_HOUR + dt.minute;

export const minutesToDate = (baseDate: DateTime, minutes: number): DateTime =>
  baseDate.set({
    hour: Math.floor(minutes / MINUTES_PER_HOUR),
    minute: minutes % MINUTES_PER_HOUR,
    second: 0,
    millisecond: 0,
  });

const tickStep = (range: number): number => {
  if (range < HALF_HOUR_TICK_THRESHOLD) return TICK_STEP_HALF_HOUR;
  if (range > TWO_HOUR_TICK_THRESHOLD) return TICK_STEP_TWO_HOURS;
  return TICK_STEP_HOUR;
};

export const generateHourMarkers = (
  startMinutes: number,
  endMinutes: number,
): number[] => {
  const step = tickStep(endMinutes - startMinutes);
  const firstTick = Math.ceil(startMinutes / step) * step;
  const markers: number[] = [];
  for (let m = firstTick; m <= endMinutes; m += step) {
    markers.push(m);
  }
  return markers;
};

export const clampToIntervals = (
  selection: ValidInterval,
  intervals: ValidInterval[],
): ValidInterval | null => {
  if (intervals.length === 0) return selection;
  const overlapping = intervals.filter((iv) => selection.overlaps(iv));
  const bounds = getEarliestAndLatest(overlapping);
  if (!bounds) return null;
  return selection.intersection(bounds) as ValidInterval;
};

export const getEarliestAndLatest = (
  intervals: ValidInterval[],
): ValidInterval | null => {
  if (intervals.length === 0) return null;
  const { earliest, latest } = intervals.reduce(
    (acc, interval) => ({
      earliest: interval.start < acc.earliest ? interval.start : acc.earliest,
      latest: interval.end > acc.latest ? interval.end : acc.latest,
    }),
    { earliest: intervals[0].start, latest: intervals[0].end },
  );
  return Interval.fromDateTimes(earliest, latest) as ValidInterval;
};

export const getDefaultDayIntervalsSelection = (
  intervals: ValidInterval[],
): ValidInterval | null => {
  if (intervals.length === 0) return null;
  const sorted = [...intervals].sort(
    (a, b) => a.start.valueOf() - b.start.valueOf(),
  );
  const defaultDisplayedDay = sorted[sorted.length - 1].start.toISODate();
  const defaultDisplayDayIntervals = sorted.filter(
    (iv) => iv.start.toISODate() === defaultDisplayedDay,
  );
  const bounds = getEarliestAndLatest(defaultDisplayDayIntervals);
  return bounds ? getDefaultSelection(bounds) : null;
};

export const getDefaultSelection = (
  available: ValidInterval,
): ValidInterval => {
  const proposed = Interval.after(available.start, {
    minutes: DEFAULT_SELECTION_MINUTES,
  });
  return (proposed.intersection(available) ?? available) as ValidInterval;
};

export const percentageToMinutes = (
  pct: number,
  dayStartMinutes: number,
  totalRange: number,
): number => Math.round(dayStartMinutes + pct * totalRange);

export const formatDateLabel = (dateKey: string, locale?: string): string => {
  const dt = DateTime.fromISO(dateKey);
  return dt
    .toLocaleString(DateTime.DATE_MED, { locale: locale ?? undefined })
    .toUpperCase();
};

export const formatTime = (minutes: number): string => {
  const dt = DateTime.fromObject({
    hour: Math.floor(minutes / MINUTES_PER_HOUR),
    minute: minutes % MINUTES_PER_HOUR,
  });
  return dt.toLocaleString(DateTime.TIME_24_SIMPLE);
};
